import { ViewConfig } from "@lib/views";
import { FinancialAccount } from "@common/types/financialAccount";
import { Entity } from "@common/types/entity";
import {
    AMOUNT_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { BankReconciliation } from "@common/contracts/reconciliation.contract";
import { AccountNameCell } from "./cells/AccountNameCell";
import { BalanceCell } from "./cells/BalanceCell";
import { DateCell } from "./cells/DateCell";
import { ActionsCell } from "./cells/ActionsCell";
import { EntityCell } from "./cells/EntityCell";
import { ContinuousReconciliationCell } from "./cells/ContinuousReconciliationCell";
import { BankReconciliationColumn } from "./bankReconciliationView";
import { BankReconciliationSort } from "./bankReconciliationSort";

export interface BankReconciliationRow {
    id: number | string;
    reconciliationNumber: number;
    financialAccount: FinancialAccount;
    entity?: Entity;
    mostRecentReconciliation?: BankReconciliation;
    bankReconciliation?: BankReconciliation;
    openingDate: string;
    closingDate?: string;
    openingBalance?: number;
    closingBalance?: number | null;
    isLastReconciliation?: boolean;
    reconciliations?: BankReconciliationRow[];
}

export const bankReconciliationViewConfig: ViewConfig<
    BankReconciliationRow,
    BankReconciliationColumn,
    BankReconciliationSort
> = {
    columns: {
        [BankReconciliationColumn.ENTITY]: {
            name: "Entity",
            header: "Entity",
            accessorKey: "entity",
            cell: EntityCell,
            minSize: 80,
            maxSize: 80,
            sort: {
                asc: BankReconciliationSort.ENTITY_ASC,
                desc: BankReconciliationSort.ENTITY_DESC,
                defaultOrder: "asc",
            },
        },
        [BankReconciliationColumn.ACCOUNT_NAME]: {
            name: "Account Name",
            header: "Account Name",
            accessorKey: "financialAccount.name",
            cell: AccountNameCell,
            minSize: 400,
            maxSize: 800,
            sort: {
                asc: BankReconciliationSort.ACCOUNT_ASC,
                desc: BankReconciliationSort.ACCOUNT_DESC,
                defaultOrder: "asc",
            },
        },
        [BankReconciliationColumn.START_DATE]: {
            name: "Start Date",
            header: "Start Date",
            accessorKey: "openingDate",
            cell: DateCell,
            ...DATE_COLUMN_SIZE,
            sort: {
                asc: BankReconciliationSort.START_DATE_ASC,
                desc: BankReconciliationSort.START_DATE_DESC,
            },
        },
        [BankReconciliationColumn.BEGINNING_BALANCE]: {
            name: "Beginning Balance",
            header: "Beginning Balance",
            accessorKey: "openingBalance",
            cell: BalanceCell,
            ...AMOUNT_COLUMN_SIZE,
            sort: {
                asc: BankReconciliationSort.BEGINNING_BALANCE_ASC,
                desc: BankReconciliationSort.BEGINNING_BALANCE_DESC,
            },
        },
        [BankReconciliationColumn.END_DATE]: {
            name: "End Date",
            header: "End Date",
            accessorKey: "closingDate",
            cell: DateCell,
            ...DATE_COLUMN_SIZE,
            sort: {
                asc: BankReconciliationSort.END_DATE_ASC,
                desc: BankReconciliationSort.END_DATE_DESC,
            },
        },
        [BankReconciliationColumn.ENDING_BALANCE]: {
            name: "Ending Balance",
            header: "Ending Balance",
            accessorKey: "closingBalance",
            cell: BalanceCell,
            ...AMOUNT_COLUMN_SIZE,
            sort: {
                asc: BankReconciliationSort.ENDING_BALANCE_ASC,
                desc: BankReconciliationSort.ENDING_BALANCE_DESC,
            },
        },
        [BankReconciliationColumn.CONTINUOUS_RECONCILIATION]: {
            name: "Continuous reconciliation",
            header: "Continuous reconciliation",
            accessorKey: "financialAccount",
            cell: ContinuousReconciliationCell,
            minSize: 180,
            maxSize: 180,
        },
        [BankReconciliationColumn.ACTIONS]: {
            name: "Actions",
            header: "",
            accessorKey: "id",
            cell: ActionsCell,
            minSize: 150,
            maxSize: 150,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: BankReconciliationColumn.ACCOUNT_NAME,
        childrenKey: "reconciliations",
    },
};
